import { useState, useEffect, useRef, useCallback } from 'react';
import { SignalingService, type ParticipantInfo } from '@/services/signaling';
import { useToast } from '@/hooks/use-toast';

interface WebRTCOptions {
  sessionId: string;
  userId: string;
  displayName: string;
  enabled: boolean;
}

interface RemoteParticipant {
  peerId: string;
  displayName: string;
  stream: MediaStream;
}

export const useWebRTC = ({ sessionId, userId, displayName, enabled }: WebRTCOptions) => {
  const [remoteParticipants, setRemoteParticipants] = useState<RemoteParticipant[]>([]);
  const [connectionStates, setConnectionStates] = useState<Map<string, RTCPeerConnectionState>>(new Map());
  const { toast } = useToast();

  const signalingRef = useRef<SignalingService | null>(null);
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const localTracksRef = useRef<MediaStreamTrack[]>([]);
  const peerIdRef = useRef<string>(`peer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const iceServers: RTCConfiguration = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  const createPeerConnection = useCallback((remotePeerId: string): RTCPeerConnection => {
    console.log('[WebRTC] Creating peer connection for:', remotePeerId);
    
    const pc = new RTCPeerConnection(iceServers);

    // Add existing local tracks
    localTracksRef.current.forEach(track => {
      console.log('[WebRTC] Adding local track to new peer:', track.kind);
      pc.addTrack(track);
    });

    // Handle incoming tracks
    pc.ontrack = (event) => {
      console.log('[WebRTC] Received remote track from', remotePeerId, event.track.kind);
      
      setRemoteParticipants(prev => {
        const existing = prev.find(p => p.peerId === remotePeerId);
        if (existing) {
          // Add track to existing stream
          existing.stream.addTrack(event.track);
          return [...prev];
        } else {
          // Create new participant with stream
          const stream = new MediaStream([event.track]);
          return [...prev, {
            peerId: remotePeerId,
            displayName: remotePeerId,
            stream
          }];
        }
      });
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[WebRTC] Sending ICE candidate to', remotePeerId);
        signalingRef.current?.sendSignal(remotePeerId, {
          type: 'ice-candidate',
          data: event.candidate.toJSON()
        });
      }
    };

    // Monitor connection state
    pc.onconnectionstatechange = () => {
      console.log('[WebRTC] Connection state with', remotePeerId, ':', pc.connectionState);
      setConnectionStates(prev => new Map(prev).set(remotePeerId, pc.connectionState));

      if (pc.connectionState === 'connected') {
        toast({
          title: "Connected",
          description: `Connected to participant`,
        });
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        toast({
          title: "Connection issue",
          description: `Connection with participant ${pc.connectionState}`,
          variant: "destructive"
        });
      }
    };

    peerConnectionsRef.current.set(remotePeerId, pc);
    return pc;
  }, [toast]);

  const handleParticipantJoin = useCallback(async (participant: ParticipantInfo) => {
    console.log('[WebRTC] Participant joined:', participant.peerId);
    
    const pc = createPeerConnection(participant.peerId);
    
    try {
      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      await signalingRef.current?.sendSignal(participant.peerId, {
        type: 'offer',
        data: offer
      });
      
      console.log('[WebRTC] Sent offer to', participant.peerId);
    } catch (err) {
      console.error('[WebRTC] Error creating offer:', err);
    }
  }, [createPeerConnection]);

  const handleParticipantLeave = useCallback((peerId: string) => {
    console.log('[WebRTC] Participant left:', peerId);
    
    const pc = peerConnectionsRef.current.get(peerId);
    if (pc) {
      pc.close();
      peerConnectionsRef.current.delete(peerId);
    }
    
    setRemoteParticipants(prev => prev.filter(p => p.peerId !== peerId));
    setConnectionStates(prev => {
      const newMap = new Map(prev);
      newMap.delete(peerId);
      return newMap;
    });
  }, []);

  const handleSignal = useCallback(async (fromPeerId: string, signal: any) => {
    console.log('[WebRTC] Received signal from', fromPeerId, signal.type);
    
    let pc = peerConnectionsRef.current.get(fromPeerId);
    
    try {
      if (signal.type === 'offer') {
        if (!pc) {
          pc = createPeerConnection(fromPeerId);
        }
        
        await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        
        await signalingRef.current?.sendSignal(fromPeerId, {
          type: 'answer',
          data: answer
        });
        
        console.log('[WebRTC] Sent answer to', fromPeerId);
      } else if (signal.type === 'answer') {
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
          console.log('[WebRTC] Set remote description from answer');
        }
      } else if (signal.type === 'ice-candidate') {
        if (pc) {
          await pc.addIceCandidate(new RTCIceCandidate(signal.data));
          console.log('[WebRTC] Added ICE candidate');
        }
      }
    } catch (err) {
      console.error('[WebRTC] Error handling signal:', err);
    }
  }, [createPeerConnection]);

  // Add track to all peer connections
  const addTrack = useCallback((track: MediaStreamTrack) => {
    console.log('[WebRTC] Adding track to all peers:', track.kind);
    localTracksRef.current.push(track);
    
    peerConnectionsRef.current.forEach((pc, peerId) => {
      const sender = pc.getSenders().find(s => s.track?.kind === track.kind);
      if (sender) {
        console.log('[WebRTC] Replacing track for', peerId);
        sender.replaceTrack(track);
      } else {
        console.log('[WebRTC] Adding new track for', peerId);
        pc.addTrack(track);
      }
    });
  }, []);

  // Remove track from all peer connections
  const removeTrack = useCallback((track: MediaStreamTrack) => {
    console.log('[WebRTC] Removing track from all peers:', track.kind);
    localTracksRef.current = localTracksRef.current.filter(t => t !== track);
    
    peerConnectionsRef.current.forEach((pc) => {
      const sender = pc.getSenders().find(s => s.track === track);
      if (sender) {
        pc.removeTrack(sender);
      }
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const initSignaling = async () => {
      try {
        console.log('[WebRTC] Initializing signaling...');
        const signaling = new SignalingService(sessionId, peerIdRef.current);
        signalingRef.current = signaling;

        signaling.onSignal(handleSignal);
        signaling.onParticipantJoin(handleParticipantJoin);
        signaling.onParticipantLeave(handleParticipantLeave);

        await signaling.join(userId, displayName);
        
        // Get existing participants and connect to them
        const participants = await signaling.getParticipants();
        console.log('[WebRTC] Found existing participants:', participants.length);
        
        for (const participant of participants) {
          await handleParticipantJoin(participant);
        }
      } catch (err) {
        console.error('[WebRTC] Error initializing:', err);
        toast({
          title: "Connection error",
          description: "Failed to join session. Please refresh and try again.",
          variant: "destructive"
        });
      }
    };

    initSignaling();

    return () => {
      console.log('[WebRTC] Cleanup...');
      
      // Close all peer connections
      peerConnectionsRef.current.forEach(pc => pc.close());
      peerConnectionsRef.current.clear();
      
      // Leave signaling
      signalingRef.current?.leave();
      signalingRef.current?.cleanup();
      signalingRef.current = null;
    };
  }, [enabled, sessionId, userId, displayName, handleSignal, handleParticipantJoin, handleParticipantLeave, toast]);

  return {
    remoteParticipants,
    connectionStates,
    addTrack,
    removeTrack,
    peerId: peerIdRef.current
  };
};
